import React from 'react';
import {
  SideNav,
  SideNavContainer,
  FilterContainer,
  AddUserContainer,
  TotalUsersText,
  ButtonBox,
  SearchBox,
  UsersContainer,
  UsersBox,
  UserImage,
  UserDetailsCard,
  UserName,
  UserType,
  UserEmail,
  Loader,
  Footer,
  SeeMoreContainer,
  SeeMoreText,
  UserScrollContainer,
  SeeMoreIndicator,
} from './users-components';

import { Button, Search, Icon } from '@app/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { primaryColor } from '@app/styles';
import { useGetUsersQuery } from '@app/graphql';
import UserDetails from './users-details';
import { UserForm } from '.';
import { UserTypes } from '@app/types';

const { forwardRef, useImperativeHandle } = React;

const IconStyle = {
  marginRight: '5px',
  height: '12px',
  width: '12px',
};

type Props = {
  onUserSelect: (firebaseId: string) => void;
  onAddUserClick: () => void;
  onListLoaded: () => void;
  userTypes: UserTypes[];
  submittedUser?: any;
  titleCompanies: any;
};

const UsersSidenav = forwardRef((props: Props, ref: any) => {
  const { onUserSelect, onAddUserClick, onListLoaded, submittedUser } = props;
  const [searchText, setSearchText] = React.useState<string>('');
  const [selectedUser, setSelectedUser] = React.useState<any>({});
  const [totalRecords, setTotalrecords] = React.useState<number>(0);
  const [usersList, setUsersList] = React.useState<any[]>([]);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [totalPages, setTotalPages] = React.useState<number>(1);
  const [seeMoreloading, setSeeMoreLoading] = React.useState<boolean>(false);
  const [seeMoreClick, SetSeeMoreClick] = React.useState<boolean>(false);
  const [submitUser, setSubmitUser] = React.useState(submittedUser);
  const [isMobile, setIsMobile] = React.useState(
    window.innerWidth < 768 ? true : false,
  );
  const [showUserDetails, setShowUserDetails] = React.useState(false);
  const [showUserAdd, setShowUserAdd] = React.useState(false);
  window.addEventListener('resize', function() {
    if (window.innerWidth > 768) {
      setShowUserDetails(false);
      setIsMobile(false);
    } else if (window.innerWidth < 768) {
      setIsMobile(true);
    }
  });
  const { loading, data } = useGetUsersQuery({
    search: searchText,
    page: currentPage,
    length: 100,
  });

  const updateUserData = (newUserData: any[]) => {
    setUsersList(usersList.concat(newUserData));
    SetSeeMoreClick(false);
  };

  const resetUserData = () => {
    setTotalrecords(0);
    setCurrentPage(1);
    setUsersList([]);
  };

  function usePrevious(value: any) {
    const ref = React.useRef();
    React.useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const prevSubmittedUser: any = usePrevious(submitUser);
  const prevSearchText = usePrevious(searchText);

  const handleSearchChange = React.useCallback((value: string) => {}, []);

  const handleSearchKeyUp = React.useCallback((event: any) => {
    const value = event.target.value;
    if (event.keyCode === 13) {
      setSearchText(value);
    }
  }, []);

  const clearSearchText = () => {
    setSearchText('');
  };

  const handleUserSelected = (data: any) => {
    const { firebaseId = '' } = data;
    onUserSelect(firebaseId);
    setSelectedUser(data);
    if (isMobile) {
      setShowUserDetails(true);
    }
  };

  const loadMore = () => {
    if (currentPage < totalPages) {
      SetSeeMoreClick(true);
      setSeeMoreLoading(true);
      setCurrentPage(currentPage + 1);
    }
  };

  useImperativeHandle(ref, () => ({
    selectFirstUser() {
      handleUserSelected(usersList[0]);
    },
  }));

  React.useEffect(() => {
    if (!loading && data && data.users && data.users.success) {
      const { user = [], totalRecords = 0, totalPages = 1 } = data.users;
      setSeeMoreLoading(false);
      setTotalrecords(totalRecords);
      setTotalPages(totalPages);
      setUsersList(user);
      onListLoaded();
      !isMobile && handleUserSelected(user.length ? user[0] : {});
      if (seeMoreClick) {
        updateUserData(user);
      }
    }
    // eslint-disable-next-line
  }, [loading, data]);
  React.useEffect(() => {
    setSubmitUser(submittedUser);
  }, [submittedUser]);
  React.useEffect(() => {
    const { user: prevUser = {}, type: prevType = '' } =
      prevSubmittedUser || {};
    const { user: currentUser = {}, type: currentType = '' } = submitUser || {};
    if (
      JSON.stringify(prevUser) !== JSON.stringify(currentUser) ||
      prevType !== currentType
    ) {
      if (currentType === 'add') {
        const tempUsersList = [...usersList];
        tempUsersList.unshift(currentUser);
        setUsersList(tempUsersList);
        setTimeout(() => {
          handleUserSelected(currentUser);
          setTotalrecords(totalRecords + 1);
        }, 200);
      } else if (currentType === 'edit') {
        const tempUsersList = [...usersList];
        const userIndex = tempUsersList.findIndex(u => {
          return u.firebaseId === currentUser.firebaseId;
        });
        if (userIndex > -1) {
          Object.assign(tempUsersList[userIndex], {
            ...currentUser,
          });
        }
        setUsersList(tempUsersList);
        setTimeout(() => {
          handleUserSelected(currentUser);
        }, 200);
      } else if (currentType === 'delete') {
        const removeArrEle = (arr: Array<any>, index: number) => {
          return arr.slice(0, index).concat(arr.slice(index + 1));
        };
        const tempUsersList = [...usersList];
        const userIndex = tempUsersList.findIndex(u => {
          return u.firebaseId === currentUser.firebaseId;
        });
        if (userIndex > -1) {
          const updatedList = removeArrEle(tempUsersList, userIndex);
          setUsersList(updatedList);
          setTimeout(() => {
            handleUserSelected(updatedList[0]);
            setTotalrecords(totalRecords - 1);
          }, 200);
        }
      }
    }
    // eslint-disable-next-line
  }, [prevSubmittedUser, submitUser]);

  React.useEffect(() => {
    if (prevSearchText !== searchText && searchText) {
      resetUserData();
    }
    // eslint-disable-next-line
  }, [prevSearchText, searchText]);

  const onDeleteUser = (userData: any) => {
    setSubmitUser(userData);
  };

  const onEditUserClick = () => {
    setSelectedUser(selectedUser);
    setShowUserAdd(true);
    setShowUserDetails(false);
  };

  const onBackClick = () => {
    setShowUserDetails(false);
    setShowUserAdd(false);
  };

  const onCancelUserClick = () => {
    setShowUserDetails(true);
    setShowUserAdd(false);
  };

  const onUserSubmit = (userData: any) => {
    setShowUserDetails(true);
    setSubmitUser(userData);
    setSelectedUser(userData);
  };

  return (
    <>
      {showUserDetails ? (
        <UserDetails
          userDetails={selectedUser}
          onEditUserClick={onEditUserClick}
          onDeleteUser={onDeleteUser}
          onBackClick={onBackClick}
        />
      ) : showUserAdd ? (
        <UserForm
          onCancelUserClick={onCancelUserClick}
          userDetails={selectedUser}
          onUserSubmit={onUserSubmit}
          userTypes={props.userTypes}
          titleCompanies={props.titleCompanies}
        />
      ) : (
        <SideNav ref={ref}>
          <SideNavContainer>
            <FilterContainer>
              <AddUserContainer>
                <TotalUsersText>Total Users ({totalRecords})</TotalUsersText>
                <ButtonBox>
                  <Button
                    variant="primary"
                    size="small"
                    width="100%"
                    onClick={() => {
                      if (isMobile) {
                        setSelectedUser(undefined);
                        setShowUserAdd(true);
                      } else {
                        onAddUserClick();
                      }
                    }}>
                    <FontAwesomeIcon
                      color="#fff"
                      icon={faPlus}
                      style={IconStyle}
                    />
                    Add User
                  </Button>
                </ButtonBox>
              </AddUserContainer>
              <SearchBox>
                <Search
                  placeholder="Search"
                  value={searchText}
                  disabled={loading}
                  onValueChange={handleSearchChange}
                  onKeyUp={handleSearchKeyUp}
                  onClear={clearSearchText}
                  variant="medium"
                  autoSearch={true}
                />
              </SearchBox>
            </FilterContainer>
            {loading && !seeMoreloading ? (
              <Loader>
                <Icon name="loader" />
              </Loader>
            ) : (
              <UsersContainer>
                {!loading && !totalRecords && (
                  <Loader>
                    <TotalUsersText>No Users Found</TotalUsersText>
                  </Loader>
                )}
                <UserScrollContainer>
                  {usersList.map((user: any, i: number) => {
                    return (
                      <UsersBox
                        key={i}
                        onClick={() => handleUserSelected(user)}
                        active={
                          selectedUser &&
                          selectedUser.firebaseId === user.firebaseId
                        }>
                        <UserImage image={user.profilePicUrl} />
                        <UserDetailsCard>
                          <UserName>{`${user.firstName} ${user.lastName}`}</UserName>
                          <UserType>
                            {user.userType === 'municipality_user'
                              ? 'Municipality User'
                              : 'Professional User'}
                          </UserType>
                          <UserEmail>{`${user.email}`}</UserEmail>
                        </UserDetailsCard>
                      </UsersBox>
                    );
                  })}
                  {totalRecords && currentPage < totalPages ? (
                    seeMoreloading ? (
                      <SeeMoreIndicator>
                        <FontAwesomeIcon
                          color={primaryColor}
                          icon={faCircleNotch}
                          size="lg"
                          spin
                        />
                      </SeeMoreIndicator>
                    ) : (
                      <Footer>
                        <SeeMoreContainer onClick={loadMore}>
                          <SeeMoreText>See More</SeeMoreText>
                        </SeeMoreContainer>
                      </Footer>
                    )
                  ) : null}
                </UserScrollContainer>
              </UsersContainer>
            )}
          </SideNavContainer>
        </SideNav>
      )}
    </>
  );
});

export default UsersSidenav;
